import React, { useEffect } from 'react';

import SortButton from './SortButton';

import { TableSettingsConfig } from './types';

import { getLabelFromName } from 'crm/helpers/formUtils';

type Props = Readonly<{
    settings?: TableSettingsConfig;
    children: React.ReactNode;
}>;

export default function TableHead({ settings, children }: Props) {
    useEffect(() => {
        if (!settings) {
            return;
        }

        const headerNames = React.Children.toArray(children).map(
            (child, index) => getHeaderText(child) || `[column ${index}]`
        );

        if (headerNames.length !== settings.columnNames.length) {
            settings.setColumnNames(headerNames);
        }
    }, [children, settings]);

    const visibleHeaders = React.Children.toArray(children).map((child: React.ReactElement, index) => {
        if (settings?.hiddenColumns.includes(index)) {
            return null;
        }

        return child.type === 'th' ? child : <th key={`header${index + 1}`}>{child}</th>;
    });

    return (
        <thead>
            <tr>{visibleHeaders}</tr>
        </thead>
    );
}

function getHeaderText(node: React.ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }

    if (React.isValidElement(node)) {
        if (node.type === SortButton) {
            if (node.props.children) {
                return getHeaderText(node.props.children);
            }

            return node.props.label ?? getLabelFromName(node.props.column);
        }

        return React.Children.toArray(node.props.children).map(getHeaderText).join('');
    }

    return '';
}
